/**
 * Auth Cookie Service
 *
 * Manages authentication state when using httpOnly cookies.
 * Since httpOnly cookies can't be read by JavaScript, this service
 * provides methods to check auth status and manage user data.
 */

import axios from 'axios';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export interface AuthUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  organizationId?: string;
  organizationName?: string;
  role: string;
  isActive: boolean;
  avatar?: string;
  provider?: string;
}

class AuthCookieService {
  private isRefreshing = false;
  private refreshSubscribers: Array<(token: boolean) => void> = [];

  /**
   * Check if user is authenticated by calling the /status endpoint
   * This endpoint returns 200 OK even when not authenticated to avoid error logs
   */
  async checkAuthStatus(): Promise<{ isAuthenticated: boolean; user: AuthUser | null }> {
    try {
      const response = await axios.get(`${API_URL}/api/v1/auth/status`, {
        withCredentials: true, // Include cookies
      });

      // The status endpoint always returns 200 OK with authenticated flag
      if (response.data && response.data.authenticated && response.data.user) {
        return {
          isAuthenticated: true,
          user: response.data.user,
        };
      }

      return {
        isAuthenticated: false,
        user: null,
      };
    } catch (error) {
      // Network error or server error
      logger.debug('Auth status check failed');
      return {
        isAuthenticated: false,
        user: null,
      };
    }
  }

  /**
   * Refresh the access token using the refresh token cookie
   */
  async refreshAccessToken(): Promise<boolean> {
    // Prevent multiple simultaneous refresh attempts
    if (this.isRefreshing) {
      return new Promise((resolve) => {
        this.refreshSubscribers.push(resolve);
      });
    }

    this.isRefreshing = true;

    try {
      const response = await axios.post(
        `${API_URL}/api/v1/auth/refresh`,
        {},
        {
          withCredentials: true, // Include refresh token cookie
        }
      );

      if (response.data && response.data.accessToken) {
        // Backend sets new access token in cookie
        this.notifyRefreshSubscribers(true);
        return true;
      }

      this.notifyRefreshSubscribers(false);
      return false;
    } catch (error) {
      logger.error('Token refresh failed:', error);
      this.notifyRefreshSubscribers(false);
      return false;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Notify all subscribers waiting for token refresh
   */
  private notifyRefreshSubscribers(success: boolean) {
    this.refreshSubscribers.forEach((callback) => callback(success));
    this.refreshSubscribers = [];
  }

  /**
   * Logout user by calling backend endpoint to clear cookies
   */
  async logout(): Promise<void> {
    logger.debug('[LOGOUT] Starting logout process');

    try {
      const response = await axios.post(
        `${API_URL}/api/v1/auth/logout`,
        {},
        {
          withCredentials: true, // Include cookies for authenticated request
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data?.cookiesCleared) {
        logger.debug('[LOGOUT] Backend confirmed cookies cleared');
      }
    } catch (error: any) {
      // Log error in development only
      logger.error('[LOGOUT] Logout request failed:', error?.message);

      // Still clear local state even if backend fails
    }

    // Clear session storage
    this.clearUserData();
  }

  /**
   * Store user data in session storage (non-sensitive data only)
   * Only stores display-related fields, no sensitive authentication data
   */
  storeUserData(user: AuthUser): void {
    try {
      // Only store non-sensitive fields needed for UI display
      // Never store tokens, passwords, or other sensitive data
      const safeUserData = {
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        organizationId: user.organizationId,
        organizationName: user.organizationName,
        role: user.role,
        isActive: user.isActive,
        // Explicitly exclude any potentially sensitive fields
        // avatar and provider are optional and safe for UI purposes
        ...(user.avatar && { avatar: user.avatar }),
        ...(user.provider && { provider: user.provider })
      };

      sessionStorage.setItem('wavescoop_user', JSON.stringify(safeUserData));
    } catch (error) {
      logger.error('Failed to store user data:', error);
    }
  }

  /**
   * Retrieve user data from session storage
   * Returns only the safe subset of user data stored for UI purposes
   */
  getUserData(): AuthUser | null {
    try {
      const userData = sessionStorage.getItem('wavescoop_user');
      if (!userData) return null;

      const parsedData = JSON.parse(userData);

      // Validate that the data has expected structure
      if (!parsedData.id || !parsedData.email) {
        logger.warn('Invalid user data in session storage');
        this.clearUserData();
        return null;
      }

      return parsedData;
    } catch (error) {
      logger.error('Failed to retrieve user data:', error);
      // Clear corrupted data
      this.clearUserData();
      return null;
    }
  }

  /**
   * Clear user data from session storage
   */
  clearUserData(): void {
    try {
      sessionStorage.removeItem('wavescoop_user');
    } catch (error) {
      logger.error('Failed to clear user data:', error);
    }
  }

  /**
   * Handle authentication response from backend
   * Backend sets cookies, we just need to store user data
   */
  handleAuthSuccess(user: AuthUser): void {
    this.storeUserData(user);
  }

  /**
   * Check if we should attempt token refresh based on error
   */
  shouldRefreshToken(error: any): boolean {
    return (
      error?.response?.status === 401 &&
      !error?.config?.url?.includes('/auth/refresh') &&
      !error?.config?.url?.includes('/auth/logout') &&
      !error?.config?._retry
    );
  }

  /**
   * Add token refresh interceptor to axios instance
   */
  setupInterceptor(axiosInstance: any): void {
    axiosInstance.interceptors.response.use(
      (response: any) => response,
      async (error: any) => {
        const originalRequest = error.config;

        if (this.shouldRefreshToken(error)) {
          originalRequest._retry = true;

          const refreshSuccess = await this.refreshAccessToken();
          if (refreshSuccess) {
            // Retry original request with new token (in cookie)
            return axiosInstance(originalRequest);
          }
        }

        return Promise.reject(error);
      }
    );
  }
}

// Export singleton instance
export const authCookieService = new AuthCookieService();

// Export type for use in other files
export type { AuthCookieService };